import { ReactNode, forwardRef } from 'react';
import { css, Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { Typography, TypographyProps } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';

export interface TagProps extends TypographyProps {
  type?: 'primary' | 'grey' | 'success' | 'warning' | 'error';
  component?: TypographyProps['component'];
  role?: TypographyProps['role'];
  href?: string;
  target?: '_blank';
  children?: ReactNode;
  onClick?: TypographyProps['onClick'];
}

export const Tag = forwardRef<HTMLLabelElement, TagProps>(({ type, children, ...other }, ref) => {
  return (
    <Typography
      variant="body2"
      component="label"
      css={[
        tagStyle,
        type === 'grey' && greyStyle,
        type === 'success' && successStyle,
        type === 'warning' && warningStyle,
        type === 'error' && errorStyle,
      ]}
      className="tag"
      {...other}
      ref={ref}
    >
      {children}
    </Typography>
  );
});

const tagStyle = (theme: Theme) => css`
  display: inline-flex;
  align-items: center;
  padding: 0 0.3rem;
  font-weight: 600 !important;
  background-color: ${transparentize(0.85, theme.color.primary[100])};
  border-radius: ${borderRadius}px;
  color: ${theme.color.primary.text} !important;
  cursor: unset;
`;

const successStyle = (theme: Theme) => css`
  background-color: ${transparentize(0.85, theme.color.green[100])};
  color: ${theme.color.green.text} !important;
`;

const warningStyle = (theme: Theme) => css`
  background-color: ${transparentize(0.85, theme.color.yellow[100])};
  color: ${theme.color.red.text} !important;
`;

const errorStyle = (theme: Theme) => css`
  background-color: ${transparentize(0.85, theme.color.red[100])};
  color: ${theme.color.red.text} !important;
`;

const greyStyle = (theme: Theme) => css`
  background-color: ${transparentize(0.85, theme.color.grey[100])};
  color: ${theme.color.foreground} !important;
`;
